/**
 * Centralized MUI Component Overrides
 *
 * Consistent component styling across both legacy and modern frontends
 */

import { Components } from '@mui/material/styles';
import { typography } from './typography';
import { spacing } from './spacing';

export const components: Components = {
  // Button styling
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontSize: typography.fontSize,
        borderRadius: 4, // @border-radius-base from Bootstrap
        fontWeight: typography.fontWeightRegular,
      },
      sizeLarge: {
        padding: `${spacing.bootstrap.large.vertical}px ${spacing.bootstrap.large.horizontal}px`,
      },
      sizeMedium: {
        padding: `${spacing.bootstrap.base.vertical}px ${spacing.bootstrap.base.horizontal}px`,
      },
      sizeSmall: {
        padding: `${spacing.bootstrap.small.vertical}px ${spacing.bootstrap.small.horizontal}px`,
      },
    },
  },

  // Card styling
  MuiCard: {
    styleOverrides: {
      root: {
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        borderRadius: 4,
      },
    },
  },

  // AppBar styling
  MuiAppBar: {
    styleOverrides: {
      root: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      },
    },
  },

  // Input Label styling
  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontSize: typography.fontSize,
      },
      shrink: {
        fontSize: 16,
      },
    },
  },

  // MenuItem styling
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: typography.fontSize,
      },
    },
  },

  // Typography styling
  MuiTypography: {
    styleOverrides: {
      paragraph: {
        fontSize: typography.fontSize,
      },
      body1: {
        fontSize: typography.fontSize,
      },
    },
  },

  // Paper styling
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 4,
      },
      elevation1: {
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
      },
      elevation2: {
        boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
      },
    },
  },

  // TextField styling
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiInputBase-root': {
          fontSize: typography.fontSize,
        },
      },
    },
  },

  // Table styling (matching Bootstrap 3 table styles)
  MuiTable: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-root': {
          padding: spacing.components.tableCellPadding,
          fontSize: typography.fontSize,
        },
      },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: spacing.components.tableCellPadding,
        fontSize: typography.fontSize,
        borderColor: '#dddddd', // @table-border-color
      },
      head: {
        fontWeight: typography.fontWeightMedium,
      },
    },
  },

  // Dialog styling
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 4,
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: spacing.components.modalTitlePadding,
        fontSize: typography.h4.fontSize,
      },
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: spacing.components.modalInnerPadding,
      },
    },
  },

  // Chip styling
  MuiChip: {
    styleOverrides: {
      root: {
        fontSize: typography.fontSize,
        borderRadius: 16, // @badge-border-radius adjusted for chips
      },
    },
  },

  // Alert styling
  MuiAlert: {
    styleOverrides: {
      root: {
        fontSize: typography.fontSize,
        borderRadius: 4,
      },
    },
  },
};
